import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BulkImportType, GroupedOffersDataModel, OfferRequest } from '@dr-customer-offers-ui/lib-interfaces';
import { Actions as EffectActions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  BulkType,
  DataModel,
  DetailTableCellModel,
  NgxIntervalDataGridRowModel,
  NgxIntervalDataGridService,
  OperationalGroup,
  OperationalHours,
  TableDataTypes,
  Week,
  WeekDays
} from '@ngx-interval-data-grid';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { CookieService } from 'ngx-shared-services';
import { filter, Observable, of, take, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { BulkInput, InputTypeEnum, OffersDataInterface, TabOption } from '../../../../shared/models';
import { UIState } from '../../../../shared/models/utility';
import { DataModelService } from '../../../../shared/services/data-model.service';
import { DataViewModelService } from '../../../../shared/services/data-vm.service';
import { DateService } from '../../../../shared/services/date.service';
import { InternalService } from '../../../../shared/services/internal.service';
import { MessageService } from '../../../../shared/services/message.service';
import { MixPanelService } from '../../../../shared/services/mixpanel.service';
import { AppState } from '../../../../store';
import * as Actions from '../../../../store/actions/com.actions';
import * as Selectors from '../../../../store/selectors/com.selectors';
import { ReasonCodeConfirmComponent } from '../../../dialogs/reason-code-confirm/reason-code-confirm.component';
import { FiltersStateService } from '../../../filters/filters-state.service';

@Component({
  selector: 'dr-customer-offers-ui-open-table',
  templateUrl: './open-table.component.html',
  styleUrls: ['./open-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OpenTableComponent implements OnInit {
  protected disableConfirmButton$: Observable<boolean> = this.ngxService.getDisableSave();
  protected UIState = UIState;
  protected hideForSpain = false;

  // Convert to signals
  protected currentOpenOffers = toSignal(this.store.select(Selectors.selectCurrentOpenOffers), { initialValue: null });
  protected openOffersLoading = toSignal(this.store.select(Selectors.selectOpenOffersLoading), { initialValue: false });
  protected openOffersErrorSignal = toSignal(this.store.select(Selectors.selectOpenOffersError), { initialValue: null });
  public currentRegViewConfigSignal = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig), { initialValue: null });

  protected editModeSignal: WritableSignal<boolean> = signal(false);
  protected showDownloadUploadOptionsSignal = signal(false);
  protected dates: number[] = [];
  protected timezoneAbbr!: string;
  protected isGroupedOffersSignal = this.internalService.isGroupedOffersSignal;
  protected columns: string[] = WeekDays;
  protected displayedColumns: string[] = [];
  protected serverData!: NgxIntervalDataGridRowModel[];
  protected dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
  protected minDate!: moment.Moment;
  protected maxDate!: moment.Moment;
  public applyButtonDisabled = this.filtersStateService.isApplyButtonDisabledSignal;
  private selectedRegControl: FormControl = this.dataVMService.selectedRegFormControl;
  protected displaySelectedReg = '';
  protected dateLabel$: Observable<string | null> = of(null);

  /**
   * True when the loaded data contains price values (offer_value.price is defined).
   * Derived from the first available cell in the data — all cells in a registration
   * are consistent (either all have price or none do).
   * Controls whether the Price sub-row is shown in the table.
   */
  protected hasPriceEnabled = signal(false);
  protected isEditButtonDisplayed = computed(() => {
    const showEdit = this.currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_offer_edit;
    return showEdit && !this.editModeSignal();
  });

  protected isBulkImportAllowed = computed(() => {
    const allowImport = this.currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_offer_edit;
    return !!allowImport && !!this.currentRegViewConfigSignal()?.allow_bulk_upload;
  });

  protected showBulkOffers = computed(() => this.editModeSignal?.() && !this.isGroupedOffersSignal?.());

  // [offer_lock] External users cannot toggle the lock and cannot edit locked cells.
  // Internal users get the lock checkbox and can always edit values.
  // protected isExternalUser = computed(() => !!this.dataVMService.externalUserSignal()); // TODO thinking this as a feature flag and uncomment it when ready. Also need to fix CSS when this is uncommented.
  protected isExternalUser = computed(() => true);

  errorMessageTitle = 'Oh snap!';
  message = 'You do not have Authorization to view offers for this Registration.';

  protected userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');

  constructor(
    private store: Store<AppState>,
    private ngxService: NgxIntervalDataGridService,
    private internalService: InternalService,
    private cdr: ChangeDetectorRef,
    private dataModelService: DataModelService,
    private dataVMService: DataViewModelService,
    private mixPanelService: MixPanelService,
    private messageService: MessageService,
    public dialog: MatDialog,
    private destroyRef: DestroyRef,
    private dateService: DateService,
    private filtersStateService: FiltersStateService,
    private actions$: EffectActions,
    private cookieService: CookieService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    console.log('Is External User --> ', this.dataVMService.externalUserSignal());
    this.actions$.pipe(ofType(Actions.bulkImportCompleted), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadData());

    this.store
      .select(Selectors.selectCurrentRegViewConfig)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((config) => config !== null)
      )
      .subscribe(() => this.loadData());

    this.store
      .select(Selectors.selectCurrentOpenOffers)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((openOffersData) => openOffersData !== null)
      )
      .subscribe((value: OffersDataInterface | null) => {
        if (value) {
          this.processGroupedData(value);
          this.filtersStateService.setApplyButtonChanging(this.dataVMService.selectedRegFormControl.value);
        }
      });

    this.mixPanelService.viewTab('Open');
    this.applyBulk();

    const { defaults } = environment;
    const apiLocale = this.cookieService.getRawI18nLocale() || defaults.apiLocale;
    this.hideForSpain = apiLocale.toLowerCase() === 'es_es' || apiLocale.toLowerCase() === 'ct_es';
  }

  get selectedTabSignal(): WritableSignal<TabOption> {
    return this.dataVMService.selectedTabOptionSignal;
  }

  triggerEditMode(isEditMode: boolean): void {
    this.editModeSignal.set(isEditMode);
  }

  openExportFunctionalityDialog(): void {
    this.dataVMService.openExportFunctionalityDialog(undefined, this.minDate, this.maxDate);
  }

  // ---------------------------------------------------------------------------
  // [offer_lock] Helpers
  // ---------------------------------------------------------------------------

  /**
   * A cell's inputs are locked only for external users when offer_lock is true.
   * Internal users are never blocked by offer_lock.
   */
  protected isCellLockedForUser(cell: DetailTableCellModel | undefined): boolean {
    return this.isExternalUser() && !!cell?.offer_lock;
  }

  // ---------------------------------------------------------------------------
  // Data processing
  // ---------------------------------------------------------------------------

  private processGroupedData(value: OffersDataInterface): void {
    const timeZone = this.currentRegViewConfigSignal()?.timeZone ?? 'UTC';
    this.timezoneAbbr = moment.tz(this.currentRegViewConfigSignal()?.timeZone ?? 'UTC').zoneName();
    this.dataSource.data = this.serverData = value.values ?? [];
    this.detectPriceEnabled(value.values ?? []);

    const startDate = this.dataSource.data[0].groupedOffersIntervals
      ? moment.utc(this.dataSource.data[0].Sunday.start_date_time)
      : moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone);

    const isGrouped = !!value.values?.[0].groupedOffersIntervals;
    const hasPrice = this.hasPriceEnabled();
    this.dates = [];
    // this.displayedColumns = value.values?.[0].groupedOffersIntervals ? ['time_period', ...this.columns] : ['offer', 'time_period', ...this.columns];
    if (isGrouped) {
      this.displayedColumns = ['time_period', ...this.columns];
    } else if (hasPrice) {
      this.displayedColumns = ['offer', 'time_period', 'offer_name', ...this.columns];
    } else {
      this.displayedColumns = ['offer', 'time_period', ...this.columns];
    }

    for (let i = 0; i < 7; i++) {
      const newDate = startDate.clone().add(i, 'days');
      this.dates.push(newDate.date());
    }

    const start = this.dataSource.data[0].groupedOffersIntervals
      ? this.dataSource.data[0].Sunday.start_date_time
      : moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    const end = this.dataSource.data[0].groupedOffersIntervals
      ? this.dataSource.data[0].Saturday.start_date_time
      : moment(this.dataSource.data[0].Saturday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    this.dateLabel$ = of(`${start} - ${end}`);

    this.editModeSignal.set(false);
    this.cdr.detectChanges();
  }

  /**
   * Walk through the first row of data and check if any day-cell has
   * `price` defined (not undefined). A single hit is enough to enable
   * the price sub-row for the entire table.
   */
  private detectPriceEnabled(rows: NgxIntervalDataGridRowModel[]): void {
    if (!rows.length) {
      this.hasPriceEnabled.set(false);
      return;
    }

    const firstRow = rows[0];
    const priceFound = WeekDays.some((day) => {
      const cell: DetailTableCellModel = firstRow[day as Week];
      return cell && cell.price !== undefined;
    });

    this.hasPriceEnabled.set(priceFound);
  }

  // ---------------------------------------------------------------------------
  // Load data
  // ---------------------------------------------------------------------------

  private loadData(): void {
    this.ngxService.clearUnSavedData();

    const selectedDates = this.dataVMService.getAppliedWeekRange();
    const regConfig = this.currentRegViewConfigSignal();

    if (!regConfig || !selectedDates || !selectedDates.start || !selectedDates.end || this.selectedRegControl.value === null) {
      console.warn('Missing required data for loading offers');
      return;
    }

    if (regConfig && !regConfig.graph_view && this.selectedRegControl.value.id) {
      const selectedDatesISO = this.dateService.getISODate(selectedDates, regConfig.timeZone);
      this.store.dispatch(
        Actions.loadOpenOffers({
          registrationId: regConfig.registrationId,
          startDate: selectedDatesISO.start,
          endDate: selectedDatesISO.end
        })
      );
    }

    this.displaySelectedReg = regConfig.display_labels || '';
  }

  // ---------------------------------------------------------------------------
  // Cell input handlers — Quantity
  // ---------------------------------------------------------------------------

  /**
   * Called on blur of a quantity input cell.
   * Validates the new value against min/max and updates the service state.
   */
  inputToCellValue(element: NgxIntervalDataGridRowModel, inputEvent: FocusEvent, week: string): void {
    const target = inputEvent?.target as HTMLInputElement | null;
    const value: number | null = target?.valueAsNumber === undefined || isNaN(target.valueAsNumber) ? null : target?.valueAsNumber;

    const oldData: NgxIntervalDataGridRowModel = this.ngxService
      .getUntouchedDataFromServer()
      .tableNgxIntervalData[TableDataTypes.OPEN].find(
        (f: NgxIntervalDataGridRowModel) => f.timePeriodKey === element.timePeriodKey
      ) as NgxIntervalDataGridRowModel;

    const nullReset = oldData[week as Week].value === null && value === null;
    const isValid =
      nullReset ||
      (value !== null &&
        value <= (this.currentRegViewConfigSignal()?.maximumValue ?? 0) &&
        value >= (this.currentRegViewConfigSignal()?.minimumValue ?? 0));

    if (value !== null && this.currentRegViewConfigSignal()?.gateRules.full_day_offer_value) {
      this.serverData = [...this.ngxService.updateValueBulk(BulkType.DAY, value, TableDataTypes.OPEN, week as Week, undefined, undefined, !isValid)];
      this.dataSource.data = this.serverData;
      this.cdr.detectChanges();
    } else {
      const updatedDataElement: NgxIntervalDataGridRowModel[] = this.ngxService.updateElementValue({
        weekName: week as Week,
        timePeriodKey: element.timePeriodKey,
        value,
        invalid_value: !isValid,
        tableDataType: TableDataTypes.OPEN
      });
      this.serverData = [...updatedDataElement];
    }
  }

  // ---------------------------------------------------------------------------
  // Cell input handlers — Price
  // ---------------------------------------------------------------------------

  /**
   * Called on blur of a price input cell.
   * For now price min is 0 and there is no upper cap from regConfig
   * (price limits are not yet in the config model). Adjust once the
   * backend exposes price_minimum / price_maximum in RegistrationViewConfigResponse.
   */
  inputToCellPrice(element: NgxIntervalDataGridRowModel, inputEvent: FocusEvent, week: string): void {
    const target = inputEvent?.target as HTMLInputElement | null;
    const price: number | null = target?.valueAsNumber === undefined || isNaN(target.valueAsNumber) ? null : target?.valueAsNumber;

    const oldData: NgxIntervalDataGridRowModel = this.ngxService
      .getUntouchedDataFromServer()
      .tableNgxIntervalData[TableDataTypes.OPEN].find(
        (f: NgxIntervalDataGridRowModel) => f.timePeriodKey === element.timePeriodKey
      ) as NgxIntervalDataGridRowModel;

    const nullReset = (oldData[week as Week].price ?? null) === null && price === null;
    // Price must be >= 0; no upper cap from config yet
    const isValid = nullReset || (price !== null && price >= 0);

    if (price !== null && this.currentRegViewConfigSignal()?.gateRules.full_day_offer_value) {
      // Apply price bulk-update for the whole day in the same way quantity does
      this.serverData = [...this.ngxService.updatePriceBulk(BulkType.DAY, price, TableDataTypes.OPEN, week as Week, undefined, undefined, !isValid)];
      this.dataSource.data = this.serverData;
      this.cdr.detectChanges();
    } else {
      const updatedDataElement: NgxIntervalDataGridRowModel[] = this.ngxService.updateElementPrice({
        weekName: week as Week,
        timePeriodKey: element.timePeriodKey,
        price,
        invalid_price: !isValid,
        tableDataType: TableDataTypes.OPEN
      });
      this.serverData = [...updatedDataElement];
    }
  }

  // ---------------------------------------------------------------------------
  // Cell input handlers — Opt-out
  // ---------------------------------------------------------------------------

  inputToCellOptOut(element: NgxIntervalDataGridRowModel, isChecked: boolean, week: string): void {
    if (this.currentRegViewConfigSignal()?.gateRules.full_day_opt_in_out) {
      this.serverData = [...this.ngxService.updateOptOutBulk(BulkType.DAY, isChecked, TableDataTypes.OPEN, week as Week)];
      this.dataSource.data = this.serverData;
    } else {
      const updatedDataElement = this.ngxService.updateElementOptOut({
        weekName: week as Week,
        timePeriodKey: element.timePeriodKey,
        opt_out: isChecked,
        tableDataType: TableDataTypes.OPEN
      });
      this.serverData = [...updatedDataElement];
    }
  }

  // ---------------------------------------------------------------------------
  // [offer_lock] Cell input handler — Offer lock (internal users only)
  // ---------------------------------------------------------------------------

  inputToCellOfferLocked(element: NgxIntervalDataGridRowModel, isChecked: boolean, week: string): void {
    // Defensive guard: the checkbox is not rendered for external users.
    if (this.isExternalUser()) return;

    const updatedDataElement = this.ngxService.updateElementOfferLocked({
      weekName: week as Week,
      timePeriodKey: element.timePeriodKey,
      offer_lock: isChecked,
      tableDataType: TableDataTypes.OPEN
    });
    this.serverData = [...updatedDataElement];
  }

  // ---------------------------------------------------------------------------
  // Cancel / Save
  // ---------------------------------------------------------------------------

  cancel(): void {
    const data: DataModel[] | OperationalGroup[] = this.ngxService.getUntouchedDataFromServer().dataModelRef[TableDataTypes.OPEN];
    this.ngxService.clearUnSavedData();

    if (this.isDataModelArray(data)) {
      this.dataSource.data = this.ngxService.groupByDayOfWeek(data, this.currentRegViewConfigSignal()?.timeZone ?? 'UTC', TableDataTypes.OPEN);
    } else if (this.isOperationalGroupArray(data)) {
      this.dataSource.data = this.ngxService.groupByIntervals(
        data,
        this.currentRegViewConfigSignal()?.operational_hours as OperationalHours[],
        TableDataTypes.OPEN
      );
    }
    this.editModeSignal.set(false);
  }

  private isDataModelArray(data: DataModel[] | OperationalGroup[]): data is DataModel[] {
    return data.length > 0 && 'product_id' in data[0] && 'program_id' in data[0];
  }

  private isOperationalGroupArray(data: DataModel[] | OperationalGroup[]): data is OperationalGroup[] {
    return data.length > 0 && 'date' in data[0] && 'groups' in data[0];
  }

  save(): void {
    const hasReasons =
      this.currentRegViewConfigSignal()?.gateRules &&
      Array.isArray(this.currentRegViewConfigSignal()?.gateRules.post_gate_edits.post_gate_change_reasons) &&
      (this.currentRegViewConfigSignal()?.gateRules.post_gate_edits.post_gate_change_reasons?.length ?? 0) > 0;
    const provideChangeReason = this.currentRegViewConfigSignal()?.gateRules.post_gate_edits.provide_change_reason;

    if (provideChangeReason && hasReasons) {
      const dialogRef = this.dialog.open(ReasonCodeConfirmComponent, {
        data: {
          postGateChangeReasons: this.currentRegViewConfigSignal()?.gateRules.post_gate_edits.post_gate_change_reasons
        },
        width: '24.375rem',
        disableClose: false,
        hasBackdrop: true
      });

      dialogRef
        .afterClosed()
        .pipe(
          tap((reason: string) => {
            if (reason) {
              this.dispatchSave(reason);
            } else {
              this.cancel();
            }
          }),
          take(1)
        )
        .subscribe();
    } else {
      this.dispatchSave();
    }
  }

  /**
   * Determines which save path to take (grouped vs interval) and fires it.
   * Extracted to avoid duplication between the reason-code and no-reason paths.
   */
  private dispatchSave(reason = ''): void {
    if (this.ngxService.unsavedData[0]?.end_date_time) {
      const unsavedOffers: DetailTableCellModel[] = this.dataVMService.lookForDSTDuplication(
        this.ngxService.unsavedData,
        this.currentRegViewConfigSignal()?.intervalFrequency
      );
      const offersToPost: OfferRequest[] = this.dataModelService.getOffersToPost(unsavedOffers, this.currentRegViewConfigSignal(), reason);
      this.postOffers(offersToPost);
    } else {
      const groupedOffersToPUT: GroupedOffersDataModel[] = this.dataModelService.getGroupedOffersToPUT(this.ngxService.unsavedData);
      this.putGroupedOffers(groupedOffersToPUT);
    }
  }

  postOffers(offers: OfferRequest[]): void {
    const regId = offers.length ? offers[0].registration_id : '';
    this.dataVMService.postOffers(offers, regId).subscribe({
      next: () => {
        this.messageService.handleSuccess(this.translate.instant('COM.TABS.OPEN.CHART.SUCCESS_TEXT'));
        this.loadData();
      },
      error: () => this.loadData()
    });
  }

  putGroupedOffers(offers: GroupedOffersDataModel[]): void {
    this.dataVMService.putGroupedOffers(offers, this.currentRegViewConfigSignal()?.registrationId as string).subscribe({
      next: () => {
        this.messageService.handleSuccess(this.translate.instant('COM.TABS.OPEN.CHART.SUCCESS_TEXT'));
        this.loadData();
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.handleError(error.error.details);
        this.loadData();
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Bulk operations
  // ---------------------------------------------------------------------------

  applyBulkOptout(day: string, isChecked: boolean): void {
    this.serverData = [...this.ngxService.updateOptOutBulk(BulkType.DAY, isChecked, TableDataTypes.OPEN, day as Week)];
    this.dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
  }

  applyBulk(): void {
    this.internalService.getBulkInputs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((b: BulkInput | null) => {
      if (!b) return;
      switch (b.bulkType) {
        case BulkType.CUSTOM: {
          if (b.inputType === InputTypeEnum.VALUE && b.value !== null && b.value !== undefined) {
            this.serverData = [
              ...this.ngxService.updateValueBulk(
                BulkType.CUSTOM,
                b.value,
                TableDataTypes.OPEN,
                undefined,
                moment(b.startDateAndTime),
                moment(b.endDateAndTime),
                false,
                b.operation
              )
            ];
          } else if (b.opt_out) {
            this.serverData = [
              ...this.ngxService.updateOptOutBulk(
                BulkType.CUSTOM,
                b.opt_out,
                TableDataTypes.OPEN,
                undefined,
                moment(b.startDateAndTime),
                moment(b.endDateAndTime),
                b.operation
              )
            ];
          } else if (b.offer_lock) {
            this.serverData = [
              ...this.ngxService.updateOfferLockBulk(
                b.offer_lock,
                TableDataTypes.OPEN,
                undefined,
                moment(b.startDateAndTime),
                moment(b.endDateAndTime),
                b.operation
              )
            ];
          }
          break;
        }
        case BulkType.DAY: {
          if (b.value !== null && b.value !== undefined) {
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.DAY, b.value, TableDataTypes.OPEN, b.week)];
          }
          break;
        }
        case BulkType.WEEK: {
          if (b.value !== null && b.value !== undefined) {
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.WEEK, b.value, TableDataTypes.OPEN)];
          }
          break;
        }
      }
      this.dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
      this.cdr.detectChanges();
    });
  }

  // ---------------------------------------------------------------------------
  // Download / Upload menu
  // ---------------------------------------------------------------------------

  toggleDownloadUploadOptions(event: MouseEvent): void {
    event.stopPropagation();
    this.showDownloadUploadOptionsSignal.update((show) => !show);
  }

  clickOutsideDownloadUploadOptions(): void {
    this.showDownloadUploadOptionsSignal.update((show) => (show ? false : show));
  }

  openBulkUploadFunctionalityDialog(): void {
    const regConfig = this.currentRegViewConfigSignal();
    this.dataVMService.openBulkUploadFunctionalityDialog({
      minDate: this.minDate,
      maxDate: this.maxDate,
      registrationId: regConfig?.registrationId ?? '',
      intervalFrequency: regConfig?.intervalFrequency ?? 60,
      timeZone: regConfig?.timeZone ?? 'UTC',
      importType: BulkImportType.Offers,
      registrationName: regConfig?.display_labels ?? '',
      offer_dimensions: regConfig?.graph_config?.offer_dimensions ?? null
    });
  }
}
